/**
 * Google Custom Search API Client
 *
 * Provides web search functionality for RAG (Retrieval-Augmented Generation) pattern.
 * Integrates with LLM to provide context-aware responses based on web search results.
 *
 * Setup required:
 * 1. Create Google Cloud project: https://console.cloud.google.com
 * 2. Enable "Custom Search API"
 * 3. Create API credentials (API key)
 * 4. Create Custom Search Engine: https://programmablesearchengine.google.com
 * 5. Configure to search "entire web"
 * 6. Add API key and CSE ID to .env.local:
 *    VITE_GOOGLE_SEARCH_API_KEY=your_api_key
 *    VITE_GOOGLE_SEARCH_ENGINE_ID=your_cse_id
 *
 * Free tier: 100 queries/day
 * Paid: $5 per 1000 queries after free tier
 */

// =============================================================================
// TYPES
// =============================================================================

export interface SearchResult {
  title: string;
  link: string;
  snippet: string;
  displayLink: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  totalResults: number;
  searchTime: number;
}

interface GoogleSearchAPIResponse {
  searchInformation?: {
    totalResults: string;
    searchTime: number;
  };
  items?: Array<{
    title: string;
    link: string;
    snippet: string;
    displayLink: string;
  }>;
  error?: {
    code: number;
    message: string;
    errors: Array<{
      domain: string;
      reason: string;
      message: string;
    }>;
  };
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const API_KEY = import.meta.env.VITE_GOOGLE_SEARCH_API_KEY || '';
const CSE_ID = import.meta.env.VITE_GOOGLE_SEARCH_ENGINE_ID || '';
const BASE_URL = 'https://www.googleapis.com/customsearch/v1';

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Check if Google Search API is configured and accessible
 */
export async function checkHealth(): Promise<boolean> {
  if (!API_KEY || !CSE_ID) {
    console.warn('Google Search API not configured. Add VITE_GOOGLE_SEARCH_API_KEY and VITE_GOOGLE_SEARCH_ENGINE_ID to .env.local');
    return false;
  }

  try {
    // Perform minimal test query
    const url = `${BASE_URL}?key=${API_KEY}&cx=${CSE_ID}&q=test&num=1`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error('Google Search API health check failed:', response.status, response.statusText);
      return false;
    }

    const data = await response.json() as GoogleSearchAPIResponse;

    if (data.error) {
      console.error('Google Search API error:', data.error.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Google Search API health check error:', error);
    return false;
  }
}

/**
 * Perform web search using Google Custom Search API
 *
 * @param query - Search query string
 * @param numResults - Number of results to return (default: 5, max: 10)
 * @returns Search results with formatted snippets
 *
 * @throws Error if API is not configured or quota exceeded
 */
export async function search(
  query: string,
  numResults: number = 5
): Promise<SearchResponse> {
  // Validation
  if (!API_KEY || !CSE_ID) {
    throw new Error('Google Search API not configured. Please add API keys to .env.local');
  }

  if (!query || query.trim().length === 0) {
    throw new Error('Search query cannot be empty');
  }

  // Limit results to max 10 (API limit)
  const num = Math.min(Math.max(numResults, 1), 10);

  try {
    // Build request URL
    const url = `${BASE_URL}?key=${API_KEY}&cx=${CSE_ID}&q=${encodeURIComponent(query)}&num=${num}`;

    console.log(`[GoogleSearch] Searching for: "${query}" (${num} results)`);

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('QUOTA_EXCEEDED: Daily search quota exceeded (100 queries/day). Try again tomorrow.');
      }
      if (response.status === 403) {
        throw new Error('API_KEY_INVALID: Invalid API key or Custom Search Engine ID');
      }
      throw new Error(`Search API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as GoogleSearchAPIResponse;

    // Check for API errors
    if (data.error) {
      if (data.error.errors?.[0]?.reason === 'quotaExceeded') {
        throw new Error('QUOTA_EXCEEDED: Daily search quota exceeded (100 queries/day). Try again tomorrow.');
      }
      throw new Error(`Google Search API error: ${data.error.message}`);
    }

    // Format results
    const results: SearchResult[] = (data.items || []).map((item) => ({
      title: item.title,
      link: item.link,
      snippet: item.snippet,
      displayLink: item.displayLink,
    }));

    const totalResults = parseInt(data.searchInformation?.totalResults || '0', 10);
    const searchTime = data.searchInformation?.searchTime || 0;

    console.log(`[GoogleSearch] Found ${results.length} results in ${searchTime}s`);

    return {
      query,
      results,
      totalResults,
      searchTime,
    };
  } catch (error) {
    // Re-throw known errors
    if (error instanceof Error && error.message.startsWith('QUOTA_EXCEEDED')) {
      throw error;
    }
    if (error instanceof Error && error.message.startsWith('API_KEY_INVALID')) {
      throw error;
    }

    // Network or other errors
    console.error('[GoogleSearch] Search failed:', error);
    throw new Error(
      error instanceof Error
        ? `Web search failed: ${error.message}`
        : 'Web search failed: Unknown error'
    );
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const googleSearchClient = {
  search,
  checkHealth,
};

export default googleSearchClient;
